/* Yatas Bedding NL — product page behaviour.
 * Variant rows, quantity stepper, image lightbox, and the sticky tab bar.
 */
(function () {
  'use strict';

  class YbnProduct extends HTMLElement {
    connectedCallback() {
      this.variants = this.readVariants();
      this.wireGallery();
      this.wireOptions();
      this.wireQuantity();
    }

    readVariants() {
      const el = this.querySelector('[data-variants]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /* Any gallery image opens a lightbox. The gallery is a static grid, so
     * there is no carousel state to keep in sync. */
    wireGallery() {
      this.querySelectorAll('.ybn-gallery__img').forEach((img) =>
        img.addEventListener('click', () => this.openLightbox(img.currentSrc || img.src))
      );
    }

    openLightbox(src) {
      const box = document.createElement('div');
      box.className = 'ybn-lightbox';
      box.innerHTML =
        '<button class="ybn-lightbox__close" type="button" aria-label="Sluiten">' +
        '<svg class="ybn-icon" viewBox="0 0 32 32" fill="none"><path d="M17.7 16l5.15-5.15-1.7-1.7L16 14.3l-5.15-5.15-1.7 1.7L14.3 16 9.15 21.15l1.7 1.7L16 17.7l5.15 5.15 1.7-1.7L17.7 16z" fill="currentColor"/></svg>' +
        '</button>';

      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      box.append(img);

      const close = () => {
        box.remove();
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (e) => {
        if (e.key === 'Escape') close();
      };

      box.addEventListener('click', (e) => {
        if (e.target === box || e.target.closest('.ybn-lightbox__close')) close();
      });
      document.addEventListener('keydown', onKey);

      document.body.append(box);
      document.body.style.overflow = 'hidden';
    }

    /* ------------------------------------------------------- variant rows */
    /* Option rows open a panel that slides in from the right; choosing a
     * value applies it and closes the panel. */
    wireOptionDrawer() {
      const drawer = this.querySelector('[data-option-drawer]');
      if (!drawer) return;

      const panels = Array.from(drawer.querySelectorAll('[data-option-panel]'));

      const close = () => {
        drawer.hidden = true;
        panels.forEach((p) => (p.hidden = true));
        document.body.style.overflow = '';
      };

      this.querySelectorAll('[data-open-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = btn.dataset.openOption;
          drawer.hidden = false;
          panels.forEach((p) => (p.hidden = p.dataset.optionPanel !== idx));
          document.body.style.overflow = 'hidden';
        });
      });

      drawer
        .querySelectorAll('[data-drawer-close]')
        .forEach((el) => el.addEventListener('click', close));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !drawer.hidden) close();
      });

      // A colour tile inside a fabric-type group must also select that type,
      // since the two are separate option axes in Shopify.
      drawer.querySelectorAll('[data-also-set]').forEach((tile) => {
        tile.addEventListener('click', () => {
          const [idx, val] = (tile.dataset.alsoSet || '').split('|');
          if (idx === undefined || val === undefined) return;
          const target = this.querySelector(
            `input[name="option-${idx}"][value="${CSS.escape(val)}"]`
          );
          if (target && !target.checked) {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      this._closeOptionDrawer = close;
    }

    wireOptions() {
      this.wireOptionDrawer();

      const inputs = Array.from(this.querySelectorAll('.ybn-optionrow__input'));
      if (!inputs.length) return;

      inputs.forEach((input) =>
        input.addEventListener('change', () => {
          this.onOptionChange();
          // Picking a value closes the drawer, as the reference does.
          this._closeOptionDrawer?.();
        })
      );
    }

    onOptionChange() {
      // Radios live in the drawer panels, not in the rows, so read them by
      // option index rather than by walking a row element.
      const count = this.querySelectorAll('[data-open-option]').length;
      const chosen = [];
      for (let i = 0; i < count; i++) {
        const checked = this.querySelector(`input[name="option-${i}"]:checked`);
        chosen.push(checked ? checked.value : null);
      }

      chosen.forEach((value, i) => {
        const label = this.querySelector(`[data-selected-for="${i}"]`);
        if (label && value) label.textContent = value;

        // Keep the row's colour swatch in step with the selection.
        const sw = this.querySelector(`[data-swatch-for="${i}"]`);
        if (sw && value) {
          const base = value.split('-')[0].trim().toLowerCase().replace(/\s+/g, '-');
          sw.className = `ybn-swatch ybn-swatch--${base}`;
        }
      });

      if (!this.variants) return;

      const match = this.variants.find((v) =>
        chosen.every((value, i) => value === null || v.options[i] === value)
      );

      const input = this.querySelector('[data-variant-input]');
      const atc = this.querySelector('[data-atc]');
      const priceEl = this.querySelector('[data-price]');

      if (!match) {
        if (atc) {
          atc.disabled = true;
          atc.textContent = atc.dataset.unavailableLabel || 'Niet beschikbaar';
        }
        return;
      }

      if (input) input.value = match.id;
      if (priceEl && match.price_html) {
        const now = priceEl.querySelector('.ybn-price__now');
        if (now) now.textContent = match.price_html;
      }
      if (atc) {
        atc.disabled = !match.available;
        atc.textContent = match.available
          ? atc.dataset.addLabel || 'In winkelwagen'
          : atc.dataset.soldOutLabel || 'Uitverkocht';
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    /* ---------------------------------------------------------- quantity */
    wireQuantity() {
      const input = this.querySelector('.ybn-qty__input');
      if (!input) return;

      const step = (delta) => {
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => step(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => step(1));
    }
  }

  /* Sticky tab bar under the buy box. */
  class YbnTabs extends HTMLElement {
    connectedCallback() {
      const buttons = Array.from(this.querySelectorAll('[data-tab]'));
      const panels = Array.from(this.querySelectorAll('[data-panel]'));
      if (!buttons.length) return;

      buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.tab;
          buttons.forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-selected', String(on));
          });
          panels.forEach((p) => p.classList.toggle('is-active', p.dataset.panel === key));
        });
      });
    }
  }

  if (!customElements.get('ybn-product')) customElements.define('ybn-product', YbnProduct);
  if (!customElements.get('ybn-tabs')) customElements.define('ybn-tabs', YbnTabs);
})();
